
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./Courses.css";

const courseStages = [
  {
    number: "01",
    level: "PRE-PRIMARY EDUCATION",
    title: "Kindergarten",
    classes: "LKG & UKG",
    icon: "bi-puzzle-fill",
    color: "sky",
    description:
      "The pre-primary programme focuses on foundational learning through play-based and activity-oriented methods. Children develop language skills, early numeracy, creativity, social interaction, and good habits in a joyful learning environment.",
    features: [
      "Play-based learning",
      "Early language and numeracy",
      "Creative activities",
      "Social interaction and good habits"
    ]
  },
  {
    number: "02",
    level: "FOUNDATIONAL ACADEMIC GROWTH",
    title: "Primary Stage",
    classes: "Classes I – V",
    icon: "bi-book-half",
    color: "gold",
    description:
      "The primary curriculum emphasises literacy, numeracy, communication, creativity, and conceptual understanding through engaging classroom activities.",
    features: [
      "Language and literacy",
      "Numeracy development",
      "Communication skills",
      "Creativity and conceptual learning"
    ]
  },
  {
    number: "03",
    level: "KNOWLEDGE & SKILL DEVELOPMENT",
    title: "Middle Stage",
    classes: "Classes VI – VIII",
    icon: "bi-lightbulb-fill",
    color: "red",
    description:
      "Students strengthen their subject knowledge, critical thinking, problem-solving abilities, and practical understanding through structured learning experiences.",
    features: [
      "Subject knowledge",
      "Critical thinking",
      "Problem-solving abilities",
      "Practical understanding"
    ]
  },
  {
    number: "04",
    level: "ACADEMIC EXCELLENCE",
    title: "Secondary Stage",
    classes: "Classes IX – X",
    icon: "bi-mortarboard-fill",
    color: "dark",
    description:
      "The secondary curriculum focuses on academic excellence, analytical thinking, and preparation for the CBSE Class X Board Examination.",
    features: [
      "Advanced subject learning",
      "Analytical thinking",
      "Academic preparation",
      "CBSE Class X Board Examination"
    ]
  }
];

function Courses() {
  useEffect(() => {
    const elements = document.querySelectorAll(
      ".courses-page .animate-on-scroll"
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show-animation");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -40px 0px"
      }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <main className="courses-page">

      {/* HERO SECTION */}
      <section className="courses-hero">
        <div className="container">
          <div className="courses-hero-content animate-on-scroll">

            <h1>
              Our <span>Courses</span>
            </h1>

            <p>
              Building strong foundations, developing essential skills
              and inspiring young minds from Kindergarten to Class X.
            </p>

            <div className="courses-breadcrumb">
              <Link to="/">Home</Link>
              <i className="bi bi-chevron-right"></i>
              <span>Courses</span>
            </div>

          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="courses-intro section-space">
        <div className="container">
          <div className="row align-items-center g-5">

            <div className="col-lg-5">
              <div className="courses-intro-visual animate-on-scroll animate-left">

                <div className="courses-visual-icon">
                  <i className="bi bi-journal-bookmark-fill"></i>
                </div>

                <div className="courses-visual-content">
                  <span>CBSE Curriculum</span>
                  <h3>Learning Today. Leading Tomorrow.</h3>
                  <p>
                    A journey of knowledge, confidence and
                    all-round development.
                  </p>
                </div>

                <div className="courses-visual-circle circle-one"></div>
                <div className="courses-visual-circle circle-two"></div>

                <div className="courses-visual-star">
                  <i className="bi bi-stars"></i>
                </div>

              </div>
            </div>

            <div className="col-lg-7">
              <div className="courses-intro-content animate-on-scroll animate-right">

                <span className="courses-label">
                  <i className="bi bi-stars"></i>
                  About Our Curriculum
                </span>

                <h2>
                  Education That Builds
                  <span> Strong Foundations</span>
                </h2>

                <p>
                  Air Force School, Vayu Sena Nagar, Nagpur, provides
                  quality education from Kindergarten to Class X,
                  following the curriculum prescribed by the Central
                  Board of Secondary Education (CBSE).
                </p>

                <p>
                  The school focuses on building a strong academic
                  foundation, developing essential life skills, and
                  nurturing the holistic growth of every child.
                </p>

                <div className="courses-intro-points">
                  <div>
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Kindergarten to Class X</span>
                  </div>
                  <div>
                    <i className="bi bi-check-circle-fill"></i>
                    <span>CBSE Curriculum</span>
                  </div>
                  <div>
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Holistic Student Development</span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* COURSE STAGES */}
      <section className="courses-stages section-space">
        <div className="container">

          <div className="courses-section-heading text-center animate-on-scroll">

            <span className="courses-label">
              <i className="bi bi-grid-fill"></i>
              Our Academic Programmes
            </span>

            <h2>
              Explore Our <span>Learning Stages</span>
            </h2>

            <p>
              A progressive learning journey designed to support
              students at every stage of their educational growth.
            </p>

          </div>

          <div className="courses-stage-grid">
            {courseStages.map((stage) => (
              <div className="course-stage-column" key={stage.number}>
                <article className={`course-stage-card ${stage.color} animate-on-scroll`}>

                  <div className="course-stage-top">
                    <span className="course-stage-number">
                      {stage.number}
                    </span>

                    <div className="course-stage-icon">
                      <i className={`bi ${stage.icon}`}></i>
                    </div>
                  </div>

                  <span className="course-stage-level">
                    {stage.level}
                  </span>

                  <h3>{stage.title}</h3>

                  <span className="course-stage-classes">
                    {stage.classes}
                  </span>

                  <div className="course-stage-divider"></div>

                  <p>{stage.description}</p>

                  <ul>
                    {stage.features.map((feature, index) => (
                      <li key={index}>
                        <i className="bi bi-check2-circle"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="course-stage-bottom"></div>

                </article>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* LEARNING JOURNEY */}
      <section className="courses-journey section-space">
        <div className="container">

          <div className="courses-journey-heading text-center animate-on-scroll">
            <span className="courses-label">
              <i className="bi bi-signpost-split-fill"></i>
              The Learning Journey
            </span>

            <h2>
              Growing Through <span>Every Stage</span>
            </h2>

            <p>
              From early childhood learning to secondary education,
              every stage helps students move forward with confidence.
            </p>
          </div>

          <div className="journey-flow animate-on-scroll">

            {courseStages.map((stage, index) => (
              <React.Fragment key={stage.number}>
                <div className={`journey-step journey-${stage.color}`}>
                  <div className="journey-step-icon">
                    <i className={`bi ${stage.icon}`}></i>
                  </div>
                  <span>{stage.title}</span>
                  <small>{stage.classes}</small>
                </div>

                {index !== courseStages.length - 1 && (
                  <div className="journey-arrow">
                    <i className="bi bi-arrow-right"></i>
                  </div>
                )}
              </React.Fragment>
            ))}

          </div>

        </div>
      </section>

    </main>
  );
}

export default Courses;
